import { dayjs, dayjsPlugins } from "@carrotfertility/carotene-core"

dayjs.extend(dayjsPlugins.utc)
dayjs.extend(dayjsPlugins.timezone)

/**
 * Returns the authoritative display timezone for appointment booking and presentation,
 * defaulting to the browser's resolved timezone or "UTC" if unavailable.
 */
export function getDisplayTimeZone(override?: string): string {
  const candidate = override?.trim()
  if (candidate) {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: candidate })
      return candidate
    } catch {
      // Invalid overrides must not prevent booking; use the browser timezone instead.
    }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
}

/**
 * Computes today's start of day anchored in the specified display timezone.
 */
export function getLocalToday(timeZone: string): dayjs.Dayjs {
  return dayjs().tz(timeZone).startOf("day")
}

/**
 * Derives the calendar date (`YYYY-MM-DD`) of a slot in the specified display timezone.
 */
export function getSlotLocalDate(startTimeIso: string, timeZone: string): string {
  return dayjs(startTimeIso).tz(timeZone).format("YYYY-MM-DD")
}

/**
 * Formats a slot's time (`h:mm A`, e.g. "9:00 PM") in the specified display timezone.
 */
export function formatSlotTime(startTimeIso: string, timeZone: string): string {
  return dayjs(startTimeIso).tz(timeZone).format("h:mm A")
}

/**
 * Formats a timezone name for the given instant. Booking copy is currently English-only.
 */
function formatTimeZoneName(
  date: Date,
  timeZone: string,
  timeZoneName: "short" | "long" | "shortGeneric" | "longGeneric"
): string {
  return (
    new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value ?? timeZone
  )
}

/** A generic label such as "PT (Pacific Time)" covers both standard and daylight-saving slots. */
export function formatTimeZoneLabel(startTimeIso: string, timeZone: string): string {
  const date = new Date(startTimeIso)
  const shortName = formatTimeZoneName(date, timeZone, timeZone === "UTC" ? "short" : "shortGeneric")
  const longName = formatTimeZoneName(date, timeZone, timeZone === "UTC" ? "long" : "longGeneric")
  return shortName === longName ? shortName : `${shortName} (${longName})`
}

/**
 * Formats a booked appointment date, time, and date-specific zone (e.g. "Tue, Sep 8 · 9:00 PM EDT").
 */
export function formatAppointmentDateTime(startTimeIso: string, timeZone: string): string {
  const dateTime = dayjs(startTimeIso).tz(timeZone).format("ddd, MMM D · h:mm A")
  return `${dateTime} ${formatTimeZoneName(new Date(startTimeIso), timeZone, "short")}`
}

/**
 * Calculates the UTC date boundaries `[from, to)` needed to cover every slot that
 * lands within the local date range `[startLocalDate, endLocalDate)`.
 *
 * For timezones ahead of UTC (e.g. AEST/Tokyo), local morning slots occur on the prior
 * UTC date, so `from` is shifted back to the earliest UTC date covering `startLocalDate`.
 * For timezones behind UTC (e.g. EDT/PDT), local evening slots occur on the next
 * UTC date, so `to` is extended to ensure the exclusive boundary covers through the end of
 * `endLocalDate`.
 */
export function getAvailabilityWindow(
  startLocalDate: string,
  endLocalDate: string,
  timeZone: string
): { from: string; to: string } {
  const startUtc = dayjs.tz(startLocalDate, timeZone).startOf("day").utc()
  const endUtc = dayjs.tz(endLocalDate, timeZone).startOf("day").utc()
  const from = startUtc.format("YYYY-MM-DD")
  const to = endUtc.isSame(endUtc.startOf("day"))
    ? endUtc.format("YYYY-MM-DD")
    : endUtc.add(1, "day").startOf("day").format("YYYY-MM-DD")
  return { from, to }
}
